'use client';

import { Check, ScrollText } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/components/ui/alert-dialog';
import type { Announcement } from '@/utils/util_types';

const labels: Record<string, { label: string; confirm: string }> = {
    'zh-CN': { label: '境谜公告', confirm: '我知道了' },
    'zh-TW': { label: '境謎公告', confirm: '我知道了' },
    'en-US': { label: 'SCENIGMA NOTICE', confirm: 'Got it' },
    'ja-JP': { label: '境谜のお知らせ', confirm: '確認しました' },
    'es-ES': { label: 'AVISO DE SCENIGMA', confirm: 'Entendido' },
    'th-TH': { label: 'ประกาศ SCENIGMA', confirm: 'รับทราบ' },
    'id-ID': { label: 'PENGUMUMAN SCENIGMA', confirm: 'Mengerti' },
    'ko-KR': { label: '境谜 공지', confirm: '확인했습니다' },
    'de-DE': { label: 'SCENIGMA-HINWEIS', confirm: 'Verstanden' },
    'ru-RU': { label: 'ОБЪЯВЛЕНИЕ SCENIGMA', confirm: 'Понятно' },
    'vi-VN': { label: 'THÔNG BÁO SCENIGMA', confirm: 'Đã hiểu' },
};

export const DEBUG_ANNOUNCEMENT: Announcement = {
    id: 'debug-announcement',
    enabled: true,
    title: '给每一位追寻真相的你',
    body: '欢迎来到境谜。\n\n一盏灯，一个谜面，一次新的调查。前往发现页，挑选一份感兴趣的案卷，用你的问题一步步靠近真相。\n\n今后的版本消息与活动通知，也会在这里与你见面。\n\n境谜团队',
    translations: {
        'en-US': {
            title: 'To everyone seeking the truth',
            body: 'Welcome to Scenigma.\n\nOne lamp, one mystery, and one new investigation. Visit Discover, choose a case that catches your interest, and move toward the truth one question at a time.\n\nFuture release notes and event updates will appear here too.\n\nThe Scenigma team',
        },
        'zh-TW': {
            title: '獻給每一位追尋真相的你',
            body: '歡迎來到境謎。\n\n一盞燈、一個謎面、一次新的調查。前往發現頁，挑選一份感興趣的案卷，用你的問題一步步靠近真相。\n\n今後的版本消息與活動通知，也會在這裡與你見面。\n\n境謎團隊',
        },
        'ja-JP': {
            title: '真実を追い求めるあなたへ',
            body: 'Scenigmaへようこそ。\n\nひとつの灯り、ひとつの謎、そして新しい捜査。発見ページから気になる事件を選び、質問を重ねて真実に近づいてください。\n\n今後のアップデートやイベントのお知らせもここでお届けします。\n\nScenigmaチーム',
        },
        'ko-KR': {
            title: '진실을 찾는 모든 분께',
            body: 'Scenigma에 오신 것을 환영합니다.\n\n하나의 등불, 하나의 수수께끼, 그리고 새로운 수사. 탐색 페이지에서 관심 있는 사건을 고르고 질문을 통해 진실에 한 걸음씩 다가가 보세요.\n\n앞으로 업데이트와 이벤트 소식도 이곳에서 전해 드립니다.\n\nScenigma 팀',
        },
        'es-ES': {
            title: 'Para quienes buscan la verdad',
            body: 'Te damos la bienvenida a Scenigma.\n\nUna lámpara, un misterio y una nueva investigación. Visita Descubrir, elige un caso que te interese y acércate a la verdad pregunta a pregunta.\n\nAquí también encontrarás próximas novedades y avisos de eventos.\n\nEl equipo de Scenigma',
        },
        'th-TH': {
            title: 'ถึงทุกคนที่กำลังตามหาความจริง',
            body: 'ยินดีต้อนรับสู่ Scenigma\n\nแสงไฟหนึ่งดวง ปริศนาหนึ่งเรื่อง และการสืบสวนครั้งใหม่ ไปที่หน้าค้นพบ เลือกคดีที่สนใจ แล้วค่อย ๆ เข้าใกล้ความจริงด้วยคำถามของคุณ\n\nข่าวอัปเดตและกิจกรรมต่าง ๆ จะมาพบคุณที่นี่เช่นกัน\n\nทีม Scenigma',
        },
        'id-ID': {
            title: 'Untuk setiap pencari kebenaran',
            body: 'Selamat datang di Scenigma.\n\nSatu lampu, satu misteri, dan satu penyelidikan baru. Kunjungi Temukan, pilih kasus yang menarik, lalu dekati kebenaran sedikit demi sedikit melalui pertanyaanmu.\n\nKabar versi terbaru dan acara mendatang juga akan hadir di sini.\n\nTim Scenigma',
        },
        'de-DE': {
            title: 'Für alle, die die Wahrheit suchen',
            body: 'Willkommen bei Scenigma.\n\nEine Lampe, ein Rätsel und eine neue Ermittlung. Öffne Entdecken, wähle einen interessanten Fall und nähere dich mit jeder Frage der Wahrheit.\n\nKünftige Versionshinweise und Veranstaltungsmeldungen findest du ebenfalls hier.\n\nDas Scenigma-Team',
        },
        'ru-RU': {
            title: 'Для всех, кто ищет правду',
            body: 'Добро пожаловать в Scenigma.\n\nОдин фонарь, одна загадка и новое расследование. Откройте раздел «Обзор», выберите интересное дело и шаг за шагом приближайтесь к правде с помощью вопросов.\n\nНовости обновлений и мероприятий также будут появляться здесь.\n\nКоманда Scenigma',
        },
        'vi-VN': {
            title: 'Dành cho mọi người đang tìm kiếm sự thật',
            body: 'Chào mừng đến với Scenigma.\n\nMột ngọn đèn, một bí ẩn và một cuộc điều tra mới. Hãy vào Khám phá, chọn một vụ án bạn quan tâm và tiến gần sự thật qua từng câu hỏi.\n\nTin cập nhật phiên bản và sự kiện cũng sẽ xuất hiện tại đây.\n\nĐội ngũ Scenigma',
        },
    },
};

export function AnnouncementDialog({ announcement, languageCode, preview, saving, onConfirm }: {
    announcement: Announcement | null;
    languageCode: string;
    preview: boolean;
    saving: boolean;
    onConfirm: () => void;
}) {
    if (!announcement) return null;
    const copy = announcement.translations[languageCode]
        ?? (languageCode === 'zh-CN' || languageCode === 'zh-TW'
            ? announcement
            : announcement.translations['en-US'] ?? announcement.translations.en ?? announcement);
    const text = labels[languageCode] ?? labels['en-US'];

    return (
        <AlertDialog open>
            <AlertDialogContent
                className="flex max-h-[calc(100dvh-4rem)] w-[calc(100%-2rem)] max-w-[380px] flex-col gap-0 overflow-hidden rounded-[2px] border-lamp/25 bg-card p-0 sm:max-w-[380px] motion-reduce:animate-none"
                onEscapeKeyDown={(event) => event.preventDefault()}
                data-testid="announcement-dialog"
            >
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-32" style={{ background: 'radial-gradient(ellipse at 50% 0%, var(--lamp-glow), transparent 75%)' }} />
                <div aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-lamp" />
                <div className="relative shrink-0 px-7 pb-5 pt-7">
                    <div className="mb-5 flex items-center gap-3 text-lamp">
                        <ScrollText size={18} strokeWidth={1.3} aria-hidden="true" />
                        <span className="text-[11px]">{text.label}</span>
                        <span className="h-px flex-1 bg-lamp/20" />
                        {preview && <span className="border border-lamp/30 px-1.5 py-0.5 text-[9px]">DEBUG</span>}
                    </div>
                    <AlertDialogTitle className="text-left text-[24px] font-semibold leading-snug [overflow-wrap:anywhere]" style={{ fontFamily: 'var(--font-playfair), "Playfair Display", Georgia, serif' }}>
                        {copy.title}
                    </AlertDialogTitle>
                </div>
                <AlertDialogDescription asChild>
                    <div className="min-h-0 overflow-y-auto overscroll-contain whitespace-pre-wrap break-words px-7 pb-6 text-[14px] leading-[1.9] text-foreground/80" tabIndex={0} data-testid="announcement-body">
                        {copy.body}
                    </div>
                </AlertDialogDescription>
                <div className="shrink-0 border-t border-border px-7 py-5">
                    <button type="button" disabled={saving} onClick={onConfirm} className="flex min-h-12 w-full items-center justify-center gap-2 bg-foreground px-4 py-3 text-sm font-medium text-background transition-opacity hover:opacity-85 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lamp disabled:opacity-50">
                        <Check size={16} strokeWidth={1.5} aria-hidden="true" />{text.confirm}
                    </button>
                </div>
            </AlertDialogContent>
        </AlertDialog>
    );
}
